/**
 * What the map fetches before it can draw anything.
 */

import { request, type MapHome, type Schema, type Narrow } from '@taxi/api-client';

/** One vehicle, as the map draws it. */
export type MapDriver = Schema<'MapDriver'>;

/**
 * The way a driver will come to the pickup they are on their way to.
 *
 * The same route the passenger's app is being sent, so a dispatcher on the phone to a
 * passenger sees what they see. Whole seconds, so "8 min" is arithmetic.
 */
export type RouteAhead = Schema<'RouteAhead'>;

export type MapStyle = Schema<'MapStyle'>;

export type MapConfiguration = Schema<'MapConfiguration'>;

/** The cars one driver may drive (#370), or that the firm lets him take any. */
export type AllowedCars = Schema<'AllowedCars'>;

export const mapApi = {
  allowedCars: (driverId: string) =>
    request<AllowedCars>(`/api/fleet/drivers/${driverId}/allowed-cars`),

  /** The tile source and where to open. Never hardcoded: this console serves every tenant. */
  configuration: () => request<MapConfiguration>('/api/geo/map-style'),

  drivers: () => request<MapDriver[]>('/api/dispatch/map'),

  /**
   * Undefined — a 204 — for a driver with nothing to draw: between jobs, already at the
   * pickup, carrying somebody, or not reporting.
   */
  route: (driverId: string) => request<RouteAhead | undefined>(`/api/dispatch/map/${driverId}/route`),

  /**
   * Where this firm's maps open, as the API resolved it: chosen, derived from the widest
   * zone, or none. Read off the anonymous tenant call rather than the map configuration,
   * because the passenger app reads the same field from the same call before sign-in.
   * `resolveMapView` in `@taxi/api-client` turns it, with the configuration, into a
   * centre and a zoom — one rule for the desk, the zone editor and the passenger's
   * fallback, instead of each guessing for itself.
   */
  home: () =>
    request<Narrow<Pick<Schema<'CurrentTenant'>, 'mapHome'>, { mapHome: MapHome }>>('/api/tenant/current').then((firm) => firm.mapHome ?? null),
};
